import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { io } from 'socket.io-client';

const PropertyContext = createContext();
const SOCKET_URL = process.env.REACT_APP_SERVER_URL || 'http://localhost:5001';

export const PropertyProvider = ({ children }) => {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    city: '', area: '', minPrice: '', maxPrice: '',
    propertyType: '', amenities: [], category: ''
  });
  const [userLocation, setUserLocation] = useState(null);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [activeTab, setActiveTab] = useState('finder'); // 'finder' | 'list'
  const [finderCategory, setFinderCategory] = useState(null); // null | 'residential' | 'commercial'
  const [viewPickerCategory, setViewPickerCategory] = useState(null); // null | 'residential' | 'commercial'
  const [socket, setSocket] = useState(null);
  const [userCity, setUserCity] = useState(null); // auto-detected city from GPS
  // Set to the place searched when it had no listings and we're showing
  // listings from elsewhere instead (so the UI can say so honestly).
  const [noResultsIn, setNoResultsIn] = useState(null);

  // Connect Socket.io once and keep it open — the handler reads the current
  // category via a ref instead of being a dependency, so switching category
  // filters no longer tears down and reopens the connection (which was
  // dropping `newProperty` events during every reconnect).
  const categoryRef = useRef(filters.category);
  useEffect(() => { categoryRef.current = filters.category; }, [filters.category]);

  useEffect(() => {
    const s = io(SOCKET_URL, { transports: ['websocket', 'polling'] });
    setSocket(s);
    s.on('newProperty', (property) => {
      // Only add to list if it matches the current category filter
      setProperties(prev => {
        const currentCategory = categoryRef.current;
        if (currentCategory && property.category !== currentCategory) return prev;
        return [property, ...prev];
      });
    });
    return () => s.disconnect();
  }, []);

  // Fetch properties — request-sequenced so a slower, older request can never
  // overwrite the UI after a newer one already resolved (e.g. rapid filter/
  // radius changes). Each call gets an id; only the latest id is allowed to
  // touch state.
  const fetchReqIdRef = useRef(0);
  // opts.track: this fetch is a search the visitor made (typed a place, Near
  // me, changed filters) — counted in Admin → Search insights. Automatic loads
  // (page open, your-city default, the zero-result fallback) are not counted.
  const userCityRef = useRef(null);
  const fetchProperties = useCallback(async (params = {}, opts = {}) => {
    const reqId = ++fetchReqIdRef.current;
    const isStale = () => reqId !== fetchReqIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const query = new URLSearchParams();
      const f = { ...filters, ...params };
      if (f.pincode) query.append('pincode', f.pincode);
      if (f.city) query.append('city', f.city);
      if (f.area) query.append('area', f.area);
      if (f.minPrice) query.append('minPrice', f.minPrice);
      if (f.maxPrice) query.append('maxPrice', f.maxPrice);
      if (f.propertyType) query.append('propertyType', f.propertyType);
      if (f.amenities?.length) query.append('amenities', f.amenities.join(','));
      if (f.category) query.append('category', f.category);
      if (f.lat) query.append('lat', f.lat);
      if (f.lng) query.append('lng', f.lng);
      if (f.radius) query.append('radius', f.radius);
      if (f.furnished) query.append('furnished', f.furnished);
      if (f.minBedrooms) query.append('minBedrooms', f.minBedrooms);
      if (opts.track) {
        query.append('track', '1');
        if (userCityRef.current) query.append('fromCity', userCityRef.current);
      }

      const res = await axios.get(`/api/properties?${query}`);
      if (isStale()) return;
      const data = res.data.data || [];

      if (data.length > 0) {
        setProperties(data);
        setNoResultsIn(null);
        return;
      }

      // Location search returned 0 — try fetching all platform listings so real
      // properties are always visible (only filter by category / type / price).
      const hadLocationFilter = f.lat || f.lng || f.pincode || f.city || f.area;
      if (hadLocationFilter) {
        const fallbackQuery = new URLSearchParams();
        if (f.category)     fallbackQuery.append('category',     f.category);
        if (f.propertyType) fallbackQuery.append('propertyType', f.propertyType);
        if (f.minPrice)     fallbackQuery.append('minPrice',     f.minPrice);
        if (f.maxPrice)     fallbackQuery.append('maxPrice',     f.maxPrice);
        if (f.amenities?.length) fallbackQuery.append('amenities', f.amenities.join(','));
        if (f.furnished)    fallbackQuery.append('furnished', f.furnished);
        if (f.minBedrooms)  fallbackQuery.append('minBedrooms', f.minBedrooms);
        const fallbackRes = await axios.get(`/api/properties?${fallbackQuery}`);
        if (isStale()) return;
        const fallbackData = fallbackRes.data.data || [];
        if (fallbackData.length > 0) {
          setProperties(fallbackData);
          setNoResultsIn(f.area || f.city || f.pincode || (f.lat ? 'your area' : 'this area'));
          return;
        }
      }

      // Nothing on the platform matches — show the real empty state, never fake listings.
      setProperties([]);
      setNoResultsIn(null);
    } catch (err) {
      if (isStale()) return;
      setError(err.response?.data?.message || 'Failed to load properties');
      setProperties([]);
      setNoResultsIn(null);
    } finally {
      if (!isStale()) setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchProperties();
  }, []);

  // City detection: low accuracy is fine (just needs city name), fast WiFi/cell fix
  const CITY_GPS_OPTS = { enableHighAccuracy: false, timeout: 8000, maximumAge: 60000 };
  // Radar/nearby: high accuracy needed for exact pin on map
  const RADAR_GPS_OPTS = { enableHighAccuracy: true, timeout: 12000, maximumAge: 0 };

  // Auto-detect user's city on mount via GPS + reverse geocode
  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(async (pos) => {
      const { latitude: lat, longitude: lng } = pos.coords;
      try {
        const res = await axios.get(
          `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`,
          { headers: { 'Accept-Language': 'en' } }
        );
        const addr = res.data.address || {};
        const city = addr.city || addr.town || addr.village || addr.state_district || '';
        if (!city) return;
        setUserCity(city);
        userCityRef.current = city;
        // Only auto-apply if user hasn't already set a location filter
        setFilters(prev => {
          if (prev.city || prev.pincode || prev.lat) return prev;
          return { ...prev, city };
        });
        fetchProperties({ city });
      } catch {}
    }, () => {}, CITY_GPS_OPTS); // eslint-disable-line
  }, []); // eslint-disable-line

  const getNearbyProperties = useCallback((radiusKm = 5) => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude: lat, longitude: lng } = pos.coords;
        setUserLocation({ lat, lng });
        fetchProperties({ lat, lng, radius: radiusKm }, { track: true });
      },
      (err) => console.warn('Geolocation error:', err.message),
      RADAR_GPS_OPTS
    );
  }, [fetchProperties]);

  // opts.track = false for navigation/reset clicks that aren't searches.
  const applyFilters = useCallback((newFilters, opts = {}) => {
    const merged = { ...filters, ...newFilters };
    setFilters(merged);
    // Always include location if we already have it, so nearby results stay nearby
    const params = { ...merged };
    if (userLocation && !params.lat) {
      params.lat = userLocation.lat;
      params.lng = userLocation.lng;
      if (!params.radius) params.radius = 5;
    }
    fetchProperties(params, { track: opts.track !== false });
  }, [fetchProperties, userLocation, filters]);

  const createProperty = useCallback(async (formData) => {
    const res = await axios.post('/api/properties', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return res.data;
  }, []);

  const revealContact = useCallback(async (id) => {
    const res = await axios.patch(`/api/properties/${id}/reveal-contact`);
    return res.data.contact;
  }, []);

  // Reset everything and go to the finder home screen (used by logo click)
  const goHome = useCallback(() => {
    setActiveTab('finder');
    setFinderCategory(null);
    setSelectedProperty(null);
    setViewPickerCategory(null);
    const resetCity = userCity || '';
    const empty = { category: '', propertyType: '', minPrice: '', maxPrice: '', amenities: [], city: resetCity, area: '', lat: '', lng: '', radius: '', pincode: '' };
    setFilters(empty);
    fetchProperties(resetCity ? { city: resetCity } : {});
  }, [fetchProperties, userCity]);

  return (
    <PropertyContext.Provider value={{
      properties, loading, error, filters, userLocation, setUserLocation,
      selectedProperty, setSelectedProperty,
      activeTab, setActiveTab,
      finderCategory, setFinderCategory,
      viewPickerCategory, setViewPickerCategory,
      fetchProperties, getNearbyProperties,
      applyFilters, setFilters,
      createProperty, revealContact,
      goHome, userCity, noResultsIn
    }}>
      {children}
    </PropertyContext.Provider>
  );
};

export const useProperty = () => useContext(PropertyContext);
